import { buildQueryParams } from "../utils/buildQueryParams";
import { APIInstance } from "./api.config";

const { api } = new APIInstance({ path: "/v1/finance" });

export const FinanceService = {
  /**
   * @function createCategory
   * @description Creates a new category with the provided data.
   * @param {IFinanceCategoryPayload} categoryData - The data containing the category details.
   * @returns {import("./api.types").IResponse<IFinanceCategory>>} - A promise that resolves to the category response.
   */
  createCategory: (categoryData) => {
    return api.post("/categories", categoryData);
  },

  /**
   * @function getCategories
   * @description Retrieves a list of categories based on the provided filters.
   * @param {{[key: string]: string | number | boolean }} [filters={}] The filters to apply to the query.
   * @returns {import("./api.types").IPaginatedResponse<IFinanceCategory>>} - A promise that resolves to the category response.
   */
  getCategories: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/categories?${params.toString()}`);
  },

  /**
   * @function getCategoryById
   * @description Retrieves a category by its ID.
   * @param {string} categoryId - The ID of the category to retrieve.
   * @returns {import("./api.types").IResponse<IFinanceCategory>>} - A promise that resolves to the category response.
   */
  getCategoryById: (categoryId) => {
    return api.get(`/categories/${categoryId}`);
  },

  /**
   * @function updateCategory
   * @description Updates an existing category with the provided data.
   * @param {string} categoryId - The ID of the category to update.
   * @param {FinanceCategory} categoryData - The data containing the category details.
   * @returns {import("./api.types").IResponse<IFinanceCategory>>} - A promise that resolves to the category response.
   */
  updateCategory: (categoryId, categoryData) => {
    return api.put(`/categories/${categoryId}`, categoryData);
  },

  /**
   * @function deleteCategory
   * @description Deletes a category by its ID.
   * @param {string} categoryId - The ID of the category to delete.
   * @returns {IDeleteResponse<any>>} - A promise that resolves to the category response.
   */
  deleteCategory: (categoryId) => {
    return api.delete(`/categories/${categoryId}`);
  },

  /**
   * @function createExpense
   * @description Creates a new expense with the provided data.
   * @param {Object} expenseData - The data containing the expense details.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the expense response.
   */
  createExpense: (payload) => {
    return api.post("/expenses", payload);
  },

  /**
   * @function getExpenses
   * @description Retrieves a list of expenses based on the provided filters.
   * @param {{[key: string]: string | number | boolean }} [filters={}] The filters to apply to the query.
   * @returns {import("./api.types").import("./api.types").IPaginatedResponse<any>>} - A promise that resolves to the expense response.
   */
  getExpenses: (filters) => {
    const params = buildQueryParams(filters);
    return api.get(`/expenses?${params.toString()}`);
  },

  /**
   * @function getExpense
   * @description Retrieves a specific expense by its ID.
   * @param {string} id - The ID of the expense to retrieve.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the expense response.
   */
  getExpense: (id) => {
    return api.get(`/expenses/${id}`);
  },

  /**
   * @function updateExpense
   * @description Updates an existing expense with the provided data.
   * @param {string} expenseId - The ID of the expense to update.
   * @param {Object} expenseDetails - The data containing the expense details.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the expense response.
   */
  updateExpense: (expenseId, expenseDetails) => {
    return api.put(`/expenses/${expenseId}`, expenseDetails);
  },

  /**
   * @function deleteExpense
   * @description Deletes an expense by its ID.
   * @param {string} id - The ID of the expense to delete.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the expense response.
   */
  deleteExpense: (id) => {
    return api.delete(`/expenses/${id}`);
  },

  /**
   * @function addIncome
   * @description Adds a new income with the provided data.
   * @param {Object} incomeData - The data containing the income details.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the income response.
   */
  addIncome: (incomeData) => {
    return api.post("/incomes", incomeData);
  },

  /**
   * @function getAllIncome
   * @description Retrieves a list of incomes based on the provided filters.
   * @param {{[key: string]: string | number | boolean }} [filters={}] The filters to apply to the query.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the income response.
   */
  getAllIncome: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/incomes?${params.toString()}`);
  },

  /**
   * @function getIncomeById
   * @description Retrieves a specific income by its ID.
   * @param {string} clientId - The ID of the income to retrieve.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the income response.
   */
  getIncomeById: (clientId) => {
    return api.get(`/incomes/${clientId}`);
  },

  /**
   * @function updateIncome
   * @description Updates an existing income with the provided data.
   * @param {string} incomeId - The ID of the income to update.
   * @param {Object} incomeData - The data containing the income details.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the income response.
   */
  updateIncome: (incomeId, incomeData) => {
    return api.put(`/incomes/${incomeId}`, incomeData);
  },

  /**
   * @function deleteIncome
   * @description Deletes an income by its ID.
   * @param {string} incomeId - The ID of the income to delete.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the income response.
   */
  deleteIncome: (incomeId) => {
    return api.delete(`/incomes/${incomeId}`);
  },

  /**
   * @function addTransaction
   * @description Adds a new transaction with the provided data.
   * @param {Object} transactionData - The data containing the transaction details.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the transaction response.
   */
  addTransaction: (transactionData) => {
    return api.post("/transactions", transactionData);
  },

  /**
   * @function getTransactions
   * @description Retrieves a list of transactions based on the provided filters.
   * @param {{[key: string]: string | number | boolean }} [filters={}] The filters to apply to the query.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the transaction response.
   */
  getTransactions: (filters = {}) => {
    const params = buildQueryParams(filters);
    return api.get(`/transactions?${params.toString()}`);
  },

  /**
   * @function getTransactionById
   * @description Retrieves a specific transaction by its ID.
   * @param {string} transactionId - The ID of the transaction to retrieve.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the transaction response.
   */
  getTransactionById: (transactionId) => {
    return api.get(`/transactions/${transactionId}`);
  },

  /**
   * @function updateTransaction
   * @description Updates an existing transaction with the provided data.
   * @param {string} transactionId - The ID of the transaction to update.
   * @param {Object} transactionData - The data containing the transaction details.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the transaction response.
   */
  updateTransaction: (transactionId, transactionData) => {
    return api.put(`/transactions/${transactionId}`, transactionData);
  },

  /**
   * @function deleteTransaction
   * @description Deletes a transaction by its ID.
   * @param {string} transactionId - The ID of the transaction to delete.
   * @returns {import("./api.types").IResponse<any>>} - A promise that resolves to the transaction response.
   */
  deleteTransaction: (transactionId) => {
    return api.delete(`/transactions/${transactionId}`);
  },
};
